import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { scheduledInspectionsApi, checklistApi, pdvApi } from '../lib/api';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, parseISO, isSunday, getYear } from 'date-fns';
import { it } from 'date-fns/locale';

// Italian national holidays (fixed dates)
const getItalianHolidays = (year) => {
  const holidays = [
    `${year}-01-01`, // Capodanno
    `${year}-01-06`, // Epifania
    `${year}-04-25`, // Festa della Liberazione
    `${year}-05-01`, // Festa dei Lavoratori
    `${year}-06-02`, // Festa della Repubblica
    `${year}-08-15`, // Ferragosto
    `${year}-11-01`, // Ognissanti
    `${year}-12-08`, // Immacolata Concezione
    `${year}-12-25`, // Natale
    `${year}-12-26`, // Santo Stefano
  ];
  
  // Easter and Easter Monday (calculate for each year)
  // Using a simplified calculation for common years
  const easterDates = {
    2024: ['2024-03-31', '2024-04-01'],
    2025: ['2025-04-20', '2025-04-21'],
    2026: ['2026-04-05', '2026-04-06'],
    2027: ['2027-03-28', '2027-03-29'],
    2028: ['2028-04-16', '2028-04-17'],
    2029: ['2029-04-01', '2029-04-02'],
    2030: ['2030-04-21', '2030-04-22'],
  };
  
  if (easterDates[year]) {
    holidays.push(...easterDates[year]);
  }
  
  return holidays;
};

const isHoliday = (date) => {
  const year = getYear(date);
  const holidays = getItalianHolidays(year);
  const dateStr = format(date, 'yyyy-MM-dd');
  return holidays.includes(dateStr);
};
import {
  CalendarDays,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  ClipboardCheck,
  User,
  Pencil,
  Trash2,
  X,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const InspectionForm = ({ inspection, pdvs, templates, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(inspection || {
    pdv_id: '',
    template_id: '',
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    scheduled_time: '09:00',
    inspector_name: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.pdv_id || !formData.template_id || !formData.inspector_name) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Unità Locale *</Label>
        <Select
          value={formData.pdv_id}
          onValueChange={(value) => setFormData({ ...formData, pdv_id: value })}
        >
          <SelectTrigger data-testid="inspection-pdv-select">
            <SelectValue placeholder="Seleziona UL" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {pdvs.map((pdv) => (
              <SelectItem key={pdv.id} value={pdv.id}>
                {pdv.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Checklist *</Label>
        <Select
          value={formData.template_id}
          onValueChange={(value) => setFormData({ ...formData, template_id: value })}
        >
          <SelectTrigger data-testid="inspection-template-select">
            <SelectValue placeholder="Seleziona checklist" />
          </SelectTrigger>
          <SelectContent>
            {templates.map((tpl) => (
              <SelectItem key={tpl.id} value={tpl.id}>
                {tpl.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Data *</Label>
          <Input
            type="date"
            value={formData.scheduled_date}
            onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
            required
            data-testid="inspection-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Ora</Label>
          <Input
            type="time"
            value={formData.scheduled_time}
            onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
            data-testid="inspection-time-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Ispettore *</Label>
        <Input
          value={formData.inspector_name}
          onChange={(e) => setFormData({ ...formData, inspector_name: e.target.value })}
          placeholder="Nome ispettore"
          required
          data-testid="inspection-inspector-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Note</Label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Note aggiuntive..."
          className="w-full min-h-[60px] px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
          data-testid="inspection-notes-input"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" data-testid="inspection-submit-btn">
          {inspection ? 'Salva Modifiche' : 'Programma Ispezione'}
        </Button>
      </div>
    </form>
  );
};

const InspectionCard = ({ inspection, onEdit, onDelete, onCancel }) => {
  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-800',
    completed: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-gray-100 text-gray-500'
  };

  const statusLabels = {
    scheduled: 'Programmata',
    completed: 'Completata',
    cancelled: 'Annullata'
  };

  return (
    <div className={`p-3 rounded-sm border ${inspection.status === 'cancelled' ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={statusColors[inspection.status]}>
              {statusLabels[inspection.status]}
            </Badge>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {inspection.scheduled_time}
            </span>
          </div>
          <p className="font-medium text-sm truncate">{inspection.pdv_name}</p>
          <p className="text-xs text-muted-foreground truncate">{inspection.template_name}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <User className="w-3 h-3" />
            {inspection.inspector_name}
          </p>
        </div>
        {inspection.status === 'scheduled' && (
          <div className="flex flex-col gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(inspection)}>
              <Pencil className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onCancel(inspection)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [inspections, setInspections] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [inspectionToCancel, setInspectionToCancel] = useState(null);

  const fetchData = async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      
      const [inspectionsRes, pdvsRes, templatesRes] = await Promise.all([
        scheduledInspectionsApi.getAll({ month, year }),
        pdvApi.getAll(),
        checklistApi.getTemplates()
      ]);
      
      setInspections(inspectionsRes.data);
      setPdvs(pdvsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  const handleSubmit = async (data) => {
    try {
      if (editingInspection) {
        await scheduledInspectionsApi.update(editingInspection.id, data);
        toast.success('Ispezione aggiornata');
      } else {
        await scheduledInspectionsApi.create(data);
        toast.success('Ispezione programmata');
      }
      setDialogOpen(false);
      setEditingInspection(null);
      setSelectedDate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleEdit = (inspection) => {
    setEditingInspection(inspection);
    setDialogOpen(true);
  };

  const handleCancelClick = (inspection) => {
    setInspectionToCancel(inspection);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!inspectionToCancel) return;
    try {
      await scheduledInspectionsApi.cancel(inspectionToCancel.id);
      toast.success('Ispezione annullata');
      setCancelDialogOpen(false);
      setInspectionToCancel(null);
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'annullamento');
    }
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setEditingInspection(null);
    setDialogOpen(true);
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days from previous month
    const startDay = start.getDay() || 7; // Convert Sunday (0) to 7
    const paddingDays = startDay - 1;
    
    const paddedDays = [];
    for (let i = paddingDays; i > 0; i--) {
      const date = new Date(start);
      date.setDate(date.getDate() - i);
      paddedDays.push({ date, isCurrentMonth: false });
    }
    
    return [
      ...paddedDays,
      ...days.map(date => ({ date, isCurrentMonth: true }))
    ];
  };

  const getInspectionsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return inspections.filter(i => i.scheduled_date === dateStr);
  };

  const days = getDaysInMonth();

  const scheduledCount = inspections.filter(i => i.status === 'scheduled').length;
  const completedCount = inspections.filter(i => i.status === 'completed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="page-header mb-0">
          <h1 className="page-title">Calendario Ispezioni</h1>
          <p className="page-subtitle">
            {scheduledCount} programmate, {completedCount} completate questo mese
          </p>
        </div>
        <Button onClick={() => { setEditingInspection(null); setSelectedDate(null); setDialogOpen(true); }} data-testid="new-inspection-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuova Ispezione
        </Button>
      </div>

      {/* Calendar Header */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="prev-month-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <CardTitle className="text-xl font-heading capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: it })}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="next-month-btn">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map(day => (
              <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map(({ date, isCurrentMonth }, index) => {
              const dayInspections = getInspectionsForDate(date);
              const hasScheduled = dayInspections.some(i => i.status === 'scheduled');
              const hasCompleted = dayInspections.some(i => i.status === 'completed');
              const isSundayOrHoliday = isSunday(date) || isHoliday(date);
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.01 }}
                  className={`min-h-[100px] p-1 border border-border rounded-sm cursor-pointer transition-colors hover:bg-secondary/50 ${
                    !isCurrentMonth ? 'bg-secondary/30 opacity-50' : ''
                  } ${isToday(date) ? 'ring-2 ring-primary' : ''} ${
                    isSundayOrHoliday && isCurrentMonth ? 'bg-red-50' : ''
                  }`}
                  onClick={() => handleDateClick(date)}
                  data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${
                      isToday(date) ? 'text-primary font-bold' : ''
                    } ${isSundayOrHoliday && isCurrentMonth ? 'text-red-600 font-semibold' : ''}`}>
                      {format(date, 'd')}
                    </span>
                    <div className="flex gap-0.5">
                      {hasScheduled && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                      {hasCompleted && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {dayInspections.slice(0, 2).map(inspection => (
                      <div
                        key={inspection.id}
                        className={`text-xs p-1 rounded truncate ${
                          inspection.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                          inspection.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-gray-100 text-gray-500'
                        }`}
                        onClick={(e) => { e.stopPropagation(); handleEdit(inspection); }}
                      >
                        {inspection.pdv_name?.split(' - ')[0] || inspection.pdv_name}
                      </div>
                    ))}
                    {dayInspections.length > 2 && (
                      <div className="text-xs text-muted-foreground text-center">
                        +{dayInspections.length - 2} altre
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Inspections List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Prossime Ispezioni
          </CardTitle>
        </CardHeader>
        <CardContent>
          {inspections.filter(i => i.status === 'scheduled').length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {inspections
                .filter(i => i.status === 'scheduled')
                .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
                .slice(0, 6)
                .map(inspection => (
                  <div key={inspection.id} className="relative">
                    <div className="absolute -top-2 -left-2 bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded">
                      {format(parseISO(inspection.scheduled_date), 'd MMM', { locale: it })}
                    </div>
                    <InspectionCard
                      inspection={inspection}
                      onEdit={handleEdit}
                      onCancel={handleCancelClick}
                    />
                  </div>
                ))}
            </div>
          ) : (
            <div className="empty-state py-8">
              <CalendarDays className="empty-state-icon" />
              <p className="empty-state-title">Nessuna ispezione programmata</p>
              <p className="empty-state-description">Clicca su una data del calendario per programmare un'ispezione</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setEditingInspection(null);
          setSelectedDate(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingInspection ? 'Modifica Ispezione' : 'Programma Ispezione'}
            </DialogTitle>
          </DialogHeader>
          <InspectionForm
            inspection={editingInspection || (selectedDate ? { scheduled_date: format(selectedDate, 'yyyy-MM-dd') } : null)}
            pdvs={pdvs}
            templates={templates}
            onSubmit={handleSubmit}
            onCancel={() => {
              setDialogOpen(false);
              setEditingInspection(null);
              setSelectedDate(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annulla Ispezione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler annullare l'ispezione programmata per "{inspectionToCancel?.pdv_name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, mantieni</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelConfirm} className="bg-destructive hover:bg-destructive/90">
              Sì, annulla
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
